/**
 * LoanEstimateViewer - Main container component for Interactive Loan Estimate
 *
 * Displays a read-only view of live Encompass LE field data in TRID format.
 * Preferred: Pass loan object with le_fields from encompass_loans table.
 * Legacy fallback: Pass extractedText from PDF for parsing.
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, FileText, CheckCircle2 } from 'lucide-react';

import { useLeFieldsMapper } from './useLeFieldsMapper';
import { useLoanEstimateParser } from './useLoanEstimateParser';
import { LEHeader } from './LEHeader';
import { LELoanSummary } from './LELoanSummary';
import { LEProjectedPayments } from './LEProjectedPayments';
import { LEClosingCosts } from './LEClosingCosts';
import { LECashToClose } from './LECashToClose';
import { LEComparisons } from './LEComparisons';
import { LELoanOfficer } from './LELoanOfficer';

import type { LoanEstimateViewerProps, ParseResult } from './types';

export const LoanEstimateViewer: React.FC<LoanEstimateViewerProps> = ({
  loan,
  extractedText,
  loanNumber,
  className
}) => {
  // Prefer live field data from loan.le_fields
  const fieldResult = useLeFieldsMapper(loan);

  // Fallback to PDF parser for legacy support
  const parserResult = useLoanEstimateParser(extractedText);

  // Use field data if available, otherwise fall back to parser
  const hasLeFields = loan?.le_fields && Object.keys(loan.le_fields).length > 0;
  const parseResult: ParseResult = hasLeFields ? fieldResult : parserResult;
  const dataSource = hasLeFields ? 'live' : 'pdf';

  // Loading state - no data at all
  if (!hasLeFields && !extractedText) {
    return (
      <div className={cn('space-y-4 p-4', className)}>
        <LEViewerSkeleton />
      </div>
    );
  }

  // Error state
  if (!parseResult.success || !parseResult.data) {
    return (
      <div className={cn('p-4', className)}>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {parseResult.error || 'Failed to parse Loan Estimate data'}
          </AlertDescription>
        </Alert>
        {parseResult.warnings.length > 0 && (
          <div className="mt-4 text-sm text-muted-foreground">
            <p className="font-medium">Warnings:</p>
            <ul className="list-disc list-inside">
              {parseResult.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  const data = parseResult.data;

  return (
    <ScrollArea className={cn('h-full', className)}>
      <div className="space-y-6 p-4">
        {/* Live Data Indicator */}
        {dataSource === 'live' && (
          <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400 px-3 py-2 rounded-md">
            <CheckCircle2 className="h-4 w-4" />
            <span>Live data from Encompass • {Object.keys(loan?.le_fields || {}).length} fields synced</span>
          </div>
        )}

        {/* Warnings Banner */}
        {parseResult.warnings.length > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Some data may be incomplete. {parseResult.warnings.length} field(s) could not
              be parsed from the document.
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <LEHeader data={data} />

        {/* Loan Summary */}
        <LELoanSummary data={data} />

        {/* Projected Payments */}
        <LEProjectedPayments data={data} />

        {/* Closing Cost Details */}
        <LEClosingCosts data={data} />

        {/* Cash to Close */}
        <LECashToClose data={data} />

        {/* Comparisons */}
        <LEComparisons data={data} />

        {/* Loan Officer */}
        <LELoanOfficer data={data} />
      </div>
    </ScrollArea>
  );
};

/**
 * Loading skeleton for the viewer
 */
const LEViewerSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-lg" />
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-48" />
      </div>
    </div>

    {/* Info box skeleton */}
    <Skeleton className="h-24 w-full rounded-lg" />

    {/* Section skeletons */}
    {[1, 2, 3].map((i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    ))}
  </div>
);

/**
 * Empty state when no LE is available
 */
export const LEViewerEmptyState: React.FC<{ message?: string }> = ({
  message = 'No Loan Estimate document found for this loan'
}) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
      <FileText className="h-8 w-8 text-muted-foreground" />
    </div>
    <p className="text-muted-foreground">{message}</p>
  </div>
);

export default LoanEstimateViewer;
